import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { formatCurrency } from '../../utils/currencyFormatter';
import { invoiceDesignTokens } from '../../utils/invoiceDesignTokens';

const formatServiceDate = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString(language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const headerCellSx = {
  color: 'text.secondary',
  fontSize: `${invoiceDesignTokens.fontSize.label}px`,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  borderBottom: '1.5px solid',
  borderBottomColor: 'divider'
};

const bodyCellSx = { fontSize: `${invoiceDesignTokens.fontSize.body}px`, color: 'text.primary' };

const CustomInvoiceLineItemsTable = ({ lineItems = [], currency, serviceDate, sx }) => {
  const { t, i18n } = useTranslation();
  const formattedServiceDate = formatServiceDate(serviceDate, i18n.language);

  return (
    <Table size="small" aria-label={t('Line items')} sx={sx}>
      <TableHead>
        <TableRow>
          <TableCell sx={headerCellSx}>{t('Service Date')}</TableCell>
          <TableCell sx={headerCellSx}>{t('Item')}</TableCell>
          <TableCell sx={headerCellSx}>{t('Description')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Unit Price')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Qty')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Discount')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Subtotal')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lineItems.map((li, idx) => (
          <TableRow
            key={li.productId ? `${li.productId}-${idx}` : idx}
            sx={{ '& > *': { borderBottom: '1px solid', borderBottomColor: 'divider' } }}
          >
            <TableCell sx={{ ...bodyCellSx, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {formattedServiceDate}
            </TableCell>
            <TableCell sx={bodyCellSx}>
              <Typography sx={{ fontSize: 'inherit' }} color="text.primary">
                {li.productName || li.description}
              </Typography>
              {li.productId && li.unit && (
                <Typography variant="caption" color="text.secondary">
                  {t('Unit')}: {li.unit}
                </Typography>
              )}
            </TableCell>
            <TableCell sx={{ color: 'text.secondary', fontSize: `${invoiceDesignTokens.fontSize.description}px` }}>
              {li.productName ? li.description : ''}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
              {formatCurrency(li.unitPrice, currency, i18n.language)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
              {li.quantity}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
              {Number(li.discount)
                ? formatCurrency(li.discount, currency, i18n.language)
                : '—'}
            </TableCell>
            <TableCell align="right" sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
              {formatCurrency(li.subtotal, currency, i18n.language)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default CustomInvoiceLineItemsTable;
